import React, { useState } from "react";
import { Dropdown, DropdownButton, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import CustomIcon from "../../assets/icons/reports_generate.svg2";
import Subheading from "../Common/Subheading";
import WidgetContainer from "../Common/WidgetContainer";
import MyModal from "../Common/MyModal.tsx";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";
import CustomReportsProjectPickerModal from "./CustomReportsProjectPickerModal";
import CustomReportsSolutionPickerModal from "./CustomReportsSolutionPickerModal";
import CustomReportsTicketingDataPickerModal from "./CustomReportsTicketingDataPickerModal";
import CustomReportsTicketingDataCountPickerModal from "./CustomReportsTicketingDataCountPickerModal";
import CustomReportsTicketStatusPickerModal from "./CustomReportsTicketStatusPickerModal";
import CustomReportsTransactionsPickerModal from "./CustomReportsTransactionsPickerModal";
import CustomReportsAssetsPickerModal from "./CustomReportsAssetsPickerModal";
import CustomReportsContactsPickerModal from "./CustomReportsContactsPickerModal";
import CustomReportsMessageHubPickerModal from "./CustomReportsMessageHubPickerModal";
import CustomReportsMonitoringPickerModal from "./CustomReportsMonitoringPickerModal";

const projectProcessesWidget = "./CustomReportsProcessesWidget.jsx";
const activeEventsWidget = "./CustomReportsEventsWidget.jsx";
const monitoringWidget = "./CustomReportsMonitoringWidget.jsx";
const ticketingDataWidget = "./CustomReportsTicketingDataWidget.jsx";
const ticketingDataCountWidget = "./CustomReportsTicketingDataCountWidget.jsx";
const ticketStatusWidget = "./CustomReportsTicketStatusWidget.jsx";
const transactionsWidget = "./CustomReportsTransactionsWidget.jsx";
const assetsWidget = "./CustomReportsAssetsWidget.jsx";
const contactsWidget = "./CustomReportsContactsWidget.jsx";
const componentMessagingWidget = "./CustomReportsComponentMessagingWidget.jsx";
const broadcastMessagingWidget = "./CustomReportsBroadcastMessagingWidget.jsx";
const inboundMessagingWidget = "./CustomReportsInboundMessagingWidget.jsx";
const projectSchedulersWidget = "./CustomReportsProjectSchedulersWidget.jsx";
const broadcastSchedulersWidget = "./CustomReportsBroadcastSchedulersWidget.jsx";

const customReportWidgets = [
  {
    value: projectProcessesWidget,
    label: "Project Processes",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: activeEventsWidget,
    label: "Active Events",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: monitoringWidget,
    label: "Monitors",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: ticketingDataWidget,
    label: "Ticketing Data",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: ticketingDataCountWidget,
    label: "Ticketing Data Count",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: ticketStatusWidget,
    label: "Tickets",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: transactionsWidget,
    label: "Transactions",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: assetsWidget,
    label: "Assets",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: contactsWidget,
    label: "Contacts",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: componentMessagingWidget,
    label: "Component Messaging",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: broadcastMessagingWidget,
    label: "Broadcast Messaging",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: inboundMessagingWidget,
    label: "Inbound Campaigns",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: projectSchedulersWidget,
    label: "Component Schedulers",
    requiresConfiguration: true,
    allowMultiple: true,
  },
  {
    value: broadcastSchedulersWidget,
    label: "Broadcast Schedulers",
    requiresConfiguration: true,
    allowMultiple: true,
  },
];

const reportsStorageKey = "customReports";

const getReports = () => {
  const reports = JSON.parse(localStorage.getItem(reportsStorageKey));
  return Array.isArray(reports) && reports.length
    ? reports
    : [{ id: "default", label: "My Report" }];
};

export default function CustomReportsPage() {
  const [widgetPicker, setWidgetPicker] = useState(null);
  const [reports, setReports] = useState(getReports);
  const confirmDialog = useConfirmDialog();
  const [activeReportId, setActiveReportId] = useState(() => getReports()[0].id);
  const activeReport = reports.find((report) => report.id === activeReportId) || reports[0];

  const saveReports = (nextReports) => {
    setReports(nextReports);
    localStorage.setItem(reportsStorageKey, JSON.stringify(nextReports));
  };

  const createReport = (label) => {
    if (!label?.trim()) return;

    const report = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      label: label.trim(),
    };
    saveReports([...reports, report]);
    setActiveReportId(report.id);
  };

  const editReportName = (report, label) => {
    if (!label?.trim() || label.trim() === report.label) return;

    saveReports(
      reports.map((item) => (item.id === report.id ? { ...item, label: label.trim() } : item))
    );
  };

  const deleteReport = async (report) => {
    if (!await confirmDialog?.("Delete report", `Delete ${report.label}?`)) return;

    const nextReports = reports.filter((item) => item.id !== report.id);
    localStorage.removeItem(`customReportsWidgetLayout-${report.id}new`);
    if (nextReports.length === 0) {
      const defaultReport = { id: "default", label: "My Report" };
      saveReports([defaultReport]);
      setActiveReportId(defaultReport.id);
      return;
    }
    saveReports(nextReports);
    if (activeReportId === report.id) setActiveReportId(nextReports[0].id);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start">
        <Subheading
          Icon={CustomIcon}
          heading="Custom Reports"
          subheading="Create and arrange widgets for your organisation's reports."
        />
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <DropdownButton
          id="custom-report-selector"
          title={activeReport.label}
          variant="light"
        >
          {reports.map((report) => (
            <Dropdown.Item
              key={report.id}
              active={report.id === activeReport.id}
              onClick={() => setActiveReportId(report.id)}
              className="d-flex align-items-center justify-content-between gap-3"
            >
              <span className="text-truncate">{report.label}</span>
              <span className="d-flex gap-2">
                <MyModal label={<FontAwesomeIcon icon={faPen} />} buttonClassName="btn-sm p-0" heading="Rename report" title={`Rename ${report.label}`}>
                  {({ handleClose }) => <ReportNameForm initialLabel={report.label} onSave={(label) => { editReportName(report, label); handleClose(); }} />}
                </MyModal>
                <button
                  type="button"
                  className="btn btn-sm p-0 text-danger"
                  onClick={(event) => { event.stopPropagation(); deleteReport(report); }}
                  title={`Delete ${report.label}`}
                  aria-label={`Delete ${report.label}`}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </span>
            </Dropdown.Item>
          ))}
          <Dropdown.Divider />
          <MyModal label="New report" buttonClassName="dropdown-item w-100 text-start py-2 px-3" heading="New report">
            {({ handleClose }) => <ReportNameForm initialLabel={`Report ${reports.length + 1}`} onSave={(label) => { createReport(label); handleClose(); }} />}
          </MyModal>
        </DropdownButton>
      </div>
      <WidgetContainer
        key={activeReport.id}
        defaultLayout={[]}
        lsField={`customReportsWidgetLayout-${activeReport.id}`}
        widgets={customReportWidgets}
        onAddWidget={({ widget, addWidget, config }) => {
          setWidgetPicker({ widget, addWidget, config });
        }}
        onEditWidget={({ widget, config, updateConfig }) => {
          setWidgetPicker({ widget, config, updateConfig });
        }}
      />
      <CustomReportsProjectPickerModal
        show={widgetPicker?.widget.value === projectProcessesWidget}
        config={widgetPicker?.config}
        onHide={() => setWidgetPicker(null)}
        onSelect={(project) => {
          const config = {
            projectId: project.ID,
            projectLabel: project.Label,
            title: project.title,
            displayMode: project.displayMode,
          };
          if (widgetPicker.updateConfig) widgetPicker.updateConfig(config);
          else widgetPicker.addWidget(widgetPicker.widget, config);
          setWidgetPicker(null);
        }}
      />
      <CustomReportsSolutionPickerModal
        show={widgetPicker?.widget.value === activeEventsWidget}
        config={widgetPicker?.config}
        onHide={() => setWidgetPicker(null)}
        onSelect={(solution) => {
          const config = {
            solutionClientUuid: solution.Uuid,
            solutionLabel: `${solution.SolutionName} ${solution.SolutionVersion}`,
            title: solution.title,
            displayMode: solution.displayMode,
            groupBy: solution.groupBy,
            statuses: solution.statuses,
            severities: solution.severities,
          };
          if (widgetPicker.updateConfig) widgetPicker.updateConfig(config);
          else widgetPicker.addWidget(widgetPicker.widget, config);
          setWidgetPicker(null);
        }}
      />
      <CustomReportsMonitoringPickerModal
        show={widgetPicker?.widget.value === monitoringWidget}
        config={widgetPicker?.config}
        onHide={() => setWidgetPicker(null)}
        onSelect={(config) => {
          if (widgetPicker.updateConfig) widgetPicker.updateConfig(config);
          else widgetPicker.addWidget(widgetPicker.widget, config);
          setWidgetPicker(null);
        }}
      />
      <CustomReportsTicketingDataPickerModal
        show={widgetPicker?.widget.value === ticketingDataWidget}
        config={widgetPicker?.config}
        onHide={() => setWidgetPicker(null)}
        onSelect={(config) => {
          if (widgetPicker.updateConfig) widgetPicker.updateConfig(config);
          else widgetPicker.addWidget(widgetPicker.widget, config);
          setWidgetPicker(null);
        }}
      />
      <CustomReportsTicketingDataCountPickerModal
        show={widgetPicker?.widget.value === ticketingDataCountWidget}
        config={widgetPicker?.config}
        onHide={() => setWidgetPicker(null)}
        onSelect={(config) => {
          if (widgetPicker.updateConfig) widgetPicker.updateConfig(config);
          else widgetPicker.addWidget(widgetPicker.widget, config);
          setWidgetPicker(null);
        }}
      />
      <CustomReportsTicketStatusPickerModal
        show={widgetPicker?.widget.value === ticketStatusWidget}
        config={widgetPicker?.config}
        onHide={() => setWidgetPicker(null)}
        onSelect={(config) => {
          if (widgetPicker.updateConfig) widgetPicker.updateConfig(config);
          else widgetPicker.addWidget(widgetPicker.widget, config);
          setWidgetPicker(null);
        }}
      />
      <CustomReportsTransactionsPickerModal
        show={widgetPicker?.widget.value === transactionsWidget}
        config={widgetPicker?.config}
        onHide={() => setWidgetPicker(null)}
        onSelect={(config) => {
          if (widgetPicker.updateConfig) widgetPicker.updateConfig(config);
          else widgetPicker.addWidget(widgetPicker.widget, config);
          setWidgetPicker(null);
        }}
      />
      <CustomReportsAssetsPickerModal
        show={widgetPicker?.widget.value === assetsWidget}
        config={widgetPicker?.config}
        onHide={() => setWidgetPicker(null)}
        onSelect={(config) => {
          if (widgetPicker.updateConfig) widgetPicker.updateConfig(config);
          else widgetPicker.addWidget(widgetPicker.widget, config);
          setWidgetPicker(null);
        }}
      />
      <CustomReportsContactsPickerModal
        show={widgetPicker?.widget.value === contactsWidget}
        config={widgetPicker?.config}
        onHide={() => setWidgetPicker(null)}
        onSelect={(config) => {
          if (widgetPicker.updateConfig) widgetPicker.updateConfig(config);
          else widgetPicker.addWidget(widgetPicker.widget, config);
          setWidgetPicker(null);
        }}
      />
      <CustomReportsMessageHubPickerModal
        show={[
          componentMessagingWidget,
          broadcastMessagingWidget,
          inboundMessagingWidget,
          projectSchedulersWidget,
          broadcastSchedulersWidget,
        ].includes(widgetPicker?.widget.value)}
        type={{
          [componentMessagingWidget]: "component",
          [broadcastMessagingWidget]: "broadcast",
          [inboundMessagingWidget]: "inbound",
          [projectSchedulersWidget]: "projectScheduler",
          [broadcastSchedulersWidget]: "broadcastScheduler",
        }[widgetPicker?.widget.value]}
        config={widgetPicker?.config}
        onHide={() => setWidgetPicker(null)}
        onSelect={(config) => {
          if (widgetPicker.updateConfig) widgetPicker.updateConfig(config);
          else widgetPicker.addWidget(widgetPicker.widget, config);
          setWidgetPicker(null);
        }}
      />
    </div>
  );
}

function ReportNameForm({ initialLabel, onSave }) {
  const [label, setLabel] = useState(initialLabel);
  return (
    <Form onSubmit={(event) => { event.preventDefault(); onSave(label); }}>
      <Form.Group className="mb-3">
        <Form.Label>Report name</Form.Label>
        <Form.Control autoFocus value={label} onChange={(event) => setLabel(event.target.value)} maxLength={100} />
      </Form.Group>
      <div className="d-flex justify-content-end">
        <button type="submit" className="btn btn-colored" disabled={!label.trim()}>
          Save report
        </button>
      </div>
    </Form>
  );
}
